import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { AxiosError } from 'axios';

// Fetch my prescriptions
export const fetchMyPrescriptions = createAsyncThunk(
  'prescriptions/fetchMy',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/prescriptions/my');
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch prescriptions');
    }
  }
);

// Fetch prescription by ID
export const fetchPrescriptionById = createAsyncThunk(
  'prescriptions/fetchById',
  async (prescriptionId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/prescriptions/${prescriptionId}`);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch prescription');
    }
  }
);

// Fetch prescriptions by appointment ID
export const fetchPrescriptionsByAppointment = createAsyncThunk(
  'prescriptions/fetchByAppointment',
  async (appointmentId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/prescriptions/appointment/${appointmentId}`);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch prescriptions');
    }
  }
);

const initialState = {
  prescriptions: [] as any[],
  currentPrescription: null as any,
  loading: false,
  error: null as string | null,
};

const prescriptionSlice = createSlice({
  name: 'prescription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPrescription: (state) => {
      state.currentPrescription = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch my prescriptions
      .addCase(fetchMyPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload;
      })
      .addCase(fetchMyPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | null;
      })
      // Fetch prescription by ID
      .addCase(fetchPrescriptionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPrescription = action.payload;
      })
      .addCase(fetchPrescriptionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | null;
      })
      // Fetch prescriptions by appointment
      .addCase(fetchPrescriptionsByAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptionsByAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload;
      })
      .addCase(fetchPrescriptionsByAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | null;
      });
  },
});

export const { clearError, clearCurrentPrescription } = prescriptionSlice.actions;
export default prescriptionSlice.reducer;